import "../navigation/ctl-menu-item.js";

const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    --ctl-font: var(--ctl-font-sans);
    --ctl-color-text: var(--ctl-color-text);
    --ctl-color-muted: var(--ctl-color-muted);
    --ctl-color-border: var(--ctl-color-border);
    --ctl-color-surface: var(--ctl-color-surface);
    --ctl-radius: var(--ctl-radius-md);
    --ctl-shadow: var(--ctl-shadow-lg);

    display: block;
    font-family: var(--ctl-font);
    color: var(--ctl-color-text);
  }

  .trigger{
    display: block;
  }

  .backdrop{
    position: fixed;
    inset: 0;
    z-index: 2999;
    display: none;
  }

  .backdrop.visible{
    display: block;
  }

  .menu{
    position: fixed;
    display: none;
    min-width: 200px;
    background: var(--ctl-color-surface);
    border: var(--ctl-border-width-thin) solid var(--ctl-color-border);
    border-radius: var(--ctl-radius);
    box-shadow: var(--ctl-shadow);
    padding: var(--ctl-space-1);
    z-index: 3000;
    outline: none;
  }

  .menu.visible{
    display: flex;
    flex-direction: column;
    gap: var(--ctl-space-0-5);
  }

  ::slotted(ctl-menu-item){
    width: 100%;
  }

  .divider{
    height: var(--ctl-border-width-thin);
    background: var(--ctl-color-border);
    margin: var(--ctl-space-1) var(--ctl-space-1);
  }
</style>

<div class="backdrop" part="backdrop" id="backdrop"></div>

<div class="trigger" part="trigger" id="trigger">
  <slot name="trigger"></slot>
</div>

<div class="menu" part="menu" id="menu" role="menu" tabindex="0">
  <slot></slot>
</div>
`;

export class CtlContextMenu extends HTMLElement {
  static get observedAttributes() {
    return ["open"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));

    this._backdrop = this.shadowRoot.getElementById("backdrop");
    this._trigger = this.shadowRoot.getElementById("trigger");
    this._menu = this.shadowRoot.getElementById("menu");

    this._onContextMenu = this._onContextMenu.bind(this);
    this._onBackdropClick = this._onBackdropClick.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onClick = this._onClick.bind(this);
    this._onSlotChange = this._onSlotChange.bind(this);

    this._menuX = 0;
    this._menuY = 0;
  }

  connectedCallback() {
    const slot = this.shadowRoot.querySelector("slot");
    slot.addEventListener("slotchange", this._onSlotChange);

    this._trigger.addEventListener("contextmenu", this._onContextMenu);
    this._backdrop.addEventListener("click", this._onBackdropClick);
    this._menu.addEventListener("keydown", this._onKeyDown);
    this._menu.addEventListener("click", this._onClick);

    queueMicrotask(() => this._initRoving());
  }

  disconnectedCallback() {
    const slot = this.shadowRoot.querySelector("slot");
    slot.removeEventListener("slotchange", this._onSlotChange);

    this._trigger.removeEventListener("contextmenu", this._onContextMenu);
    this._backdrop.removeEventListener("click", this._onBackdropClick);
    this._menu.removeEventListener("keydown", this._onKeyDown);
    this._menu.removeEventListener("click", this._onClick);
  }

  attributeChangedCallback(name) {
    if (name === "open") this._syncOpen();
  }

  // --- Public API ---
  show(x, y) {
    this._menuX = x || 0;
    this._menuY = y || 0;
    this.setAttribute("open", "");
  }

  hide() {
    this.removeAttribute("open");
  }

  toggle(x, y) {
    if (this.hasAttribute("open")) {
      this.hide();
    } else {
      this.show(x, y);
    }
  }

  // --- Internal ---
  _onSlotChange() {
    this._initRoving();
  }

  _items() {
    const slot = this.shadowRoot.querySelector("slot:not([name])");
    const els = slot.assignedElements({ flatten: true }) || [];
    return els.filter((el) => el.tagName === "CTL-MENU-ITEM");
  }

  _enabledItems() {
    return this._items().filter((it) => !it.disabled);
  }

  _initRoving() {
    const items = this._enabledItems();
    items.forEach((it) => (it.tabIndex = -1));
    if (items[0]) items[0].tabIndex = 0;
  }

  focusFirst() {
    const items = this._enabledItems();
    items.forEach((it) => (it.tabIndex = -1));
    if (items[0]) {
      items[0].tabIndex = 0;
      items[0].focus();
    }
  }

  focusLast() {
    const items = this._enabledItems();
    items.forEach((it) => (it.tabIndex = -1));
    const last = items[items.length - 1];
    if (last) {
      last.tabIndex = 0;
      last.focus();
    }
  }

  _move(delta) {
    const items = this._enabledItems();
    if (!items.length) return;

    const active = this.getRootNode().activeElement;
    const idx = items.indexOf(active);
    const next = items[(idx < 0 ? 0 : idx + delta + items.length) % items.length];

    items.forEach((it) => (it.tabIndex = -1));
    next.tabIndex = 0;
    next.focus();
  }

  _syncOpen() {
    const isOpen = this.hasAttribute("open");

    if (isOpen) {
      this._backdrop.classList.add("visible");
      this._menu.classList.add("visible");

      // Position the menu
      this._positionMenu();

      // Focus first item
      queueMicrotask(() => this.focusFirst());

      this.dispatchEvent(
        new CustomEvent("ctl-open", { bubbles: true, composed: true })
      );
    } else {
      this._backdrop.classList.remove("visible");
      this._menu.classList.remove("visible");

      this.dispatchEvent(
        new CustomEvent("ctl-close", { bubbles: true, composed: true })
      );
    }
  }

  _positionMenu() {
    // Position at stored coordinates
    const menuRect = this._menu.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let x = this._menuX;
    let y = this._menuY;

    // Adjust if menu would overflow viewport
    const margin = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--spacing-sm')) || 10;
    if (x + menuRect.width > viewportWidth) {
      x = viewportWidth - menuRect.width - margin;
    }

    if (y + menuRect.height > viewportHeight) {
      y = viewportHeight - menuRect.height - margin;
    }

    // Ensure menu doesn't go off left/top edges
    x = Math.max(margin, x);
    y = Math.max(margin, y);

    this._menu.style.left = x + "px";
    this._menu.style.top = y + "px";
  }

  _onContextMenu(e) {
    e.preventDefault();
    this.show(e.clientX, e.clientY);
  }

  _onBackdropClick() {
    this.hide();
  }

  _onKeyDown(e) {
    if (e.key === "Escape") {
      e.preventDefault();
      this.hide();
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      this._move(+1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      this._move(-1);
    } else if (e.key === "Home") {
      e.preventDefault();
      this.focusFirst();
    } else if (e.key === "End") {
      e.preventDefault();
      this.focusLast();
    } else if (e.key === "Enter" || e.key === " ") {
      const active = this.getRootNode().activeElement;
      if (active && active.tagName === "CTL-MENU-ITEM" && !active.disabled) {
        e.preventDefault();
        active.click();
      }
    }
  }

  _onClick(e) {
    const path = e.composedPath ? e.composedPath() : [];
    const item = path.find((n) => n && n.tagName === "CTL-MENU-ITEM");
    if (!item) return;
    if (item.disabled) return;

    this.dispatchEvent(
      new CustomEvent("ctl-change", {
        bubbles: true,
        composed: true,
        detail: { value: item.value, id: item.value, item }
      })
    );

    // Close menu after selection
    this.hide();
  }
}

customElements.define("ctl-context-menu", CtlContextMenu);
